// frontend/src/components/BotProfile.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, Chip, FormHelperText, Avatar, FormControlLabel, Radio, RadioGroup, IconButton } from '@mui/material';
import { ArrowBackIos as ArrowBackIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import CreateGroupModal from './CreateGroupModal';
import { getCurrentIdentity } from '../userIdentity';

const genresOptions = ['18+', 'Флирт', 'Ролевые игры', 'Сюжет', 'Общение', 'Фантазия', 'Романтика', 'Приключения'];

const BotProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [avatar, setAvatar] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [genres, setGenres] = useState([]);
  const [gender, setGender] = useState('');
  const [greeting, setGreeting] = useState('');
  const [appearance, setAppearance] = useState('');
  const [plot, setPlot] = useState('');
  const [character, setCharacter] = useState('');
  const [personality, setPersonality] = useState('');
  const [details, setDetails] = useState('');
  const [errors, setErrors] = useState({});
  const [userId, setUserId] = useState(null);
  const [isOwner, setIsOwner] = useState(false); // глобальный админ
  const [canEdit, setCanEdit] = useState(false); // может редактировать этот бот
  const [showGroupModal, setShowGroupModal] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadIdentity = async () => {
      const identity = await getCurrentIdentity();

      if (!mounted) return;

      const currentUserId = identity.userId;

      setUserId(currentUserId);
      setIsOwner(Number(currentUserId) === 865631835);
    };

    loadIdentity();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (id) {
      const loadBot = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await fetch(`https://donkeybot.store/api/bots/${id}`);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${await response.text()}`);
          }
          const data = await response.json();
          console.log('Бот загружен:', data);

          setBot(data);
          setAvatar(data.avatar || null);
          setName(data.name || '');
          setDescription(data.description || '');
          setGenres(data.genres ? data.genres.split(',').filter(Boolean) : []);
          setGender(data.gender || '');
          setGreeting(data.greeting || '');
          setAppearance(data.appearance || '');
          setPlot(data.plot || '');
          setCharacter(data.character || '');
          setPersonality(data.personality || '');
          setDetails(data.details || '');

          // Право редактировать
          const can = isOwner || (data.createdBy && data.createdBy.toString() === userId?.toString());
          setCanEdit(can);
        } catch (err) {
          console.error('Ошибка загрузки бота:', err);
          setError(err.message || 'Не удалось загрузить бота');
        } finally {
          setLoading(false);
        }
      };

      loadBot();
    }
  }, [id, userId, isOwner]);

  const validate = () => {
    const newErrors = {};
    if (name.length > 20) newErrors.name = 'Максимум 20 символов';
    if (description.length > 200) newErrors.description = 'Максимум 200 символов';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const response = await fetch(`https://donkeybot.store/api/bots/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          avatar,
          name,
          description,
          genres: genres.join(','),
          gender,
          greeting,
          appearance,
          plot,
          character,
          personality,
          details
        }),
      });

      if (response.ok) {
        alert('Бот обновлен');
        setIsEditing(false);
        // Перезагружаем данные
        const loadBot = async () => {
          try {
            const response = await fetch(`https://donkeybot.store/api/bots/${id}`);
            if (!response.ok) throw new Error(await response.text());
            const data = await response.json();
            setBot(data);
            setAvatar(data.avatar || null);
            setName(data.name || '');
            setDescription(data.description || '');
            setGenres(data.genres ? data.genres.split(',').filter(Boolean) : []);
            setGender(data.gender || '');
            setGreeting(data.greeting || '');
            setAppearance(data.appearance || '');
            setPlot(data.plot || '');
            setCharacter(data.character || '');
            setPersonality(data.personality || '');
            setDetails(data.details || '');
            const can = isOwner || (data.createdBy && data.createdBy.toString() === userId?.toString());
            setCanEdit(can);
          } catch (err) {
            console.error('Ошибка перезагрузки:', err);
          }
        };
        loadBot();
      } else {
        alert(`Ошибка: ${response.status} — ${await response.text()}`);
      }
    } catch (err) {
      alert(`Не удалось сохранить: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Удалить бота навсегда?')) return;
    try {
      const response = await fetch(`https://donkeybot.store/api/bots/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        alert('Бот удален');
        navigate('/bots');
      } else {
        alert(`Ошибка удаления: ${response.status}`);
      }
    } catch (err) {
      alert(`Не удалось удалить: ${err.message}`);
    }
  };

  const handleAvatarChange = (e) => {
    if (e.target.files?.[0]) {
      const reader = new FileReader();
      reader.onload = () => setAvatar(reader.result);
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleStartChat = async () => {
    try {
      const response = await fetch('/api/chats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, botId: id }),
      });

      if (!response.ok) {
        alert(`Ошибка чата: ${response.status}`);
        return;
      }

      const data = await response.json();
      const nextChatId = data.chatId || data.id;

      if (!nextChatId) {
        alert('Ошибка айди');
        return;
      }

      navigate(`/chat/${nextChatId}`);
    } catch (err) {
      alert(`Ошибка чата: ${err.message}`);
    }
  };

  if (loading) return <Typography sx={{ textAlign: 'center', mt: 10 }}>Загрузка...</Typography>;
  if (error) return <Typography color="error" sx={{ textAlign: 'center', mt: 10 }}>{error}</Typography>;
  if (!bot) return <Typography sx={{ textAlign: 'center', mt: 10 }}>Бот не найден</Typography>;

  return (
    <Box sx={{ p: 2, overflowY: 'auto', pb: '120px' }}>
      <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', position: 'absolute', left: 10, top: 10 }}>
  <ArrowBackIcon />
</IconButton>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
        Профиль бота
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        {isEditing ? (
          <label htmlFor="avatar-upload">
            <Avatar src={avatar} sx={{ width: 120, height: 120, cursor: 'pointer', border: '2px solid #444' }} />
            <input id="avatar-upload" type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
          </label>
        ) : (
          <Avatar src={avatar} sx={{ width: 120, height: 120, border: '2px solid #444' }} />
        )}
      </Box>

      <TextField fullWidth label="Имя" value={name} onChange={e => setName(e.target.value)} disabled={!isEditing} margin="normal" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} />
      <TextField fullWidth label="Описание" value={description} onChange={e => setDescription(e.target.value)} multiline rows={3} disabled={!isEditing} margin="normal" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} />
      <FormControl fullWidth margin="normal" error={genres.length > 3} disabled={!isEditing}>
        <InputLabel shrink sx={{ fontStyle: 'italic' }}>Жанр</InputLabel>
        <Select
          multiple
          value={genres}
          onChange={(e) => setGenres(e.target.value.slice(0, 3))}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => <Chip key={value} label={value} />)}
            </Box>
          )}
        >
          {genresOptions.map((genre) => (
            <MenuItem key={genre} value={genre}>
              {genre}
            </MenuItem>
          ))}
        </Select>
        {genres.length > 3 && <FormHelperText error>Максимум 3 жанра</FormHelperText>}
      </FormControl>
      <FormControl fullWidth margin="normal" disabled={!isEditing}>
        <InputLabel shrink sx={{ fontStyle: 'italic' }}>Пол</InputLabel>
        <RadioGroup value={gender} onChange={(e) => setGender(e.target.value)} row>
          <FormControlLabel value="Мужчина" control={<Radio />} label="Мужчина" />
          <FormControlLabel value="Женщина" control={<Radio />} label="Женщина" />
        </RadioGroup>
      </FormControl>
      <TextField label="Приветственное сообщение" value={greeting} onChange={(e) => setGreeting(e.target.value)} fullWidth multiline rows={2} margin="normal" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} disabled={!isEditing} />
      <TextField label="Внешность" value={appearance} onChange={(e) => setAppearance(e.target.value)} fullWidth multiline rows={2} margin="normal" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} disabled={!isEditing} />
      <TextField label="Сюжет" value={plot} onChange={(e) => setPlot(e.target.value)} fullWidth multiline rows={3} margin="normal" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} disabled={!isEditing} />
      <TextField label="Характер" value={character} onChange={(e) => setCharacter(e.target.value)} fullWidth multiline rows={2} margin="normal" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} disabled={!isEditing} />
      <TextField label="Личность" value={personality} onChange={(e) => setPersonality(e.target.value)} fullWidth multiline rows={2} margin="normal" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} disabled={!isEditing} />
      <TextField label="Детали" value={details} onChange={(e) => setDetails(e.target.value)} fullWidth multiline rows={3} margin="normal" InputLabelProps={{ shrink: true }} sx={{ '& .MuiFormLabel-root': { fontStyle: 'italic' } }} disabled={!isEditing} />

      {canEdit && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button variant="contained" fullWidth onClick={isEditing ? handleSave : () => setIsEditing(true)} sx={{ borderRadius: '20px', backgroundColor: 'blue' }}>
            {isEditing ? 'Сохранить' : 'Изменить'}
          </Button>
          <Button variant="contained" fullWidth onClick={handleDelete} sx={{ borderRadius: '20px', backgroundColor: 'red' }}>
            Удалить
          </Button>
        </Box>
      )}

      {/* КНОПКИ ЧАТА И ГРУППЫ */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="contained" fullWidth onClick={handleStartChat} sx={{ borderRadius: '20px' }}>
          Начать чат
        </Button>

        {/* Кнопка создания группы отображается ТОЛЬКО если bot.isDndMode === 1 */}
        {bot?.isDndMode === 1 && (
          <Button 
            variant="contained" 
            fullWidth 
            onClick={() => setShowGroupModal(true)} 
            sx={{ borderRadius: '20px', backgroundColor: '#9c27b0' }} // Фиолетовая, чтобы выделялась
          >
            Создать группу
          </Button>
        )}
      </Box>

      {/* Сама модалка */}
      <CreateGroupModal 
        open={showGroupModal}
        userId={userId}
        botId={id}
        onClose={() => setShowGroupModal(false)}
        onCreated={(newChatId) => {
          setShowGroupModal(false);
          navigate(`/chat/${newChatId}`); // Сразу кидаем юзера в новую группу
        }}
      />

    </Box>
  );
};

export default BotProfile;